import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo/Seo";
import { Reveal } from "@/components/Reveal/Reveal";
import { Parallax } from "@/components/Motion/Parallax";
import { ElfsightWidget } from "@/components/Elfsight/ElfsightWidget";
import { InquiryForm } from "@/components/Forms/InquiryForm";
import { SITE, STATS } from "@/config/site";
import { FEATURED } from "@/data/featured";
import { mediaUrl } from "@/lib/media";
import { lodgingJsonLd } from "@/lib/structuredData";
import { HeroVideo } from "./HeroVideo";
import styles from "./HomePage.module.css";

const HERO_VIDEO = "/media/hero_1.mp4";
const HERO_POSTER = "/media/hero_1-poster.jpg";

export function HomePage() {
  return (
    <>
      <Seo
        title="Home"
        description="Villa Rosa is a private Italian-inspired estate on 30 acres in Fallbrook, California — for weddings, celebrations, and luxury getaways with panoramic valley views, a romantic ceremony space, and a resort-style pool and spa."
        path="/"
        image={mediaUrl(FEATURED.heroPoster)}
        preloadImage={HERO_POSTER}
        jsonLd={lodgingJsonLd(mediaUrl(FEATURED.ceremony))}
      />

      {/* ---- Hero ---- */}
      <section className={styles.hero}>
        <div className={`container ${styles.heroInner}`}>
          <div className={styles.heroCopy}>
            <p className={styles.heroEyebrow}>Fallbrook · California</p>
            <h1 className={styles.heroTitle}>
              <span>
                <span>Villa Rosa</span>
              </span>
            </h1>
            <p className={styles.heroSubhead}>
              A private Italian-inspired estate for weddings, celebrations &amp;
              luxury getaways.
            </p>
            <div className={styles.heroCtas}>
              <Link to="/book-an-event" className="btn btn--primary">
                Plan Your Event
              </Link>
              <Link to="/book-a-stay" className="btn btn--ghost">
                Book a Private Stay
              </Link>
            </div>
          </div>
        </div>
        <div className={styles.heroMedia}>
          <HeroVideo
            src={HERO_VIDEO}
            poster={HERO_POSTER}
            className={styles.heroVideo}
          />
        </div>
      </section>

      {/* Stats */}
      <div className={styles.statBar}>
        {STATS.map((s) => (
          <div key={s.label} className={styles.stat}>
            <span className={styles.statValue}>{s.value}</span>
            <span className={styles.statUnit}>{s.unit}</span>
          </div>
        ))}
      </div>

      {/* ---- Positioning ---- */}
      <section className={styles.chapter}>
        <div className={`container ${styles.chapterGrid}`}>
          <Reveal as="div" variant="up" className={styles.chapterText}>
            <p className="eyebrow eyebrow--rule">The Villa Rosa Difference</p>
            <h2>Thirty private acres in the Fallbrook hills.</h2>
            <p className={styles.lead}>
              Tucked into 30 private acres in the hills of Fallbrook, Villa Rosa
              offers panoramic valley views, a romantic ceremony setting, a
              resort-style pool and spa, and a private estate experience designed
              for unforgettable moments.
            </p>
            <p>
              Set on 30 private acres, the estate offers a romantic outdoor
              ceremony space, hillside waterfall, elegant interiors, and curated
              event-ready areas that let guests celebrate in complete privacy.
            </p>
            <p>
              Whether you&rsquo;re planning a wedding, private event, family
              gathering, or weekend escape, Villa Rosa gives you the freedom to
              create something personal, beautiful, and unforgettable.
            </p>
          </Reveal>
          <Reveal as="div" variant="scale" delay={120} className={styles.frame}>
            <Parallax
              speed={0.16}
              className={styles.frameInner}
              style={{ backgroundImage: `url(${mediaUrl(FEATURED.frontCross)})` }}
            >
              <span />
            </Parallax>
          </Reveal>
        </div>
      </section>

      {/* ---- Weddings & Events (dark feature) ---- */}
      <section className={styles.feature}>
        <Parallax
          speed={0.12}
          className={styles.featureMedia}
          style={{ backgroundImage: `url(${mediaUrl(FEATURED.ceremony)})` }}
        >
          <span />
        </Parallax>
        <div className={styles.featureScrim} />
        <div className="container">
          <Reveal as="div" variant="up" className={styles.featureInner}>
            <p className="eyebrow eyebrow--rule">Weddings &amp; Events</p>
            <h2>A breathtaking backdrop for your big day.</h2>
            <p>
              Exchange vows beneath the cross with the valley spread out below,
              then celebrate across lawns, decks, and elegant indoor spaces. Our
              team handles the details — design, coordination, catering — so the
              day belongs entirely to you.
            </p>
            <Link to="/book-an-event" className="btn btn--primary">
              Plan Your Event
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---- Overnight Stays ---- */}
      <section className={`${styles.chapter} section--alt`}>
        <div className={`container ${styles.chapterGrid} ${styles.chapterGridReverse}`}>
          <Reveal as="div" variant="scale" className={styles.frame}>
            <Parallax
              speed={0.16}
              className={styles.frameInner}
              style={{ backgroundImage: `url(${mediaUrl(FEATURED.gameRoom)})` }}
            >
              <span />
            </Parallax>
          </Reveal>
          <Reveal as="div" variant="up" delay={120} className={styles.chapterText}>
            <p className="eyebrow eyebrow--rule">Overnight Stays</p>
            <h2>Settle in for the weekend.</h2>
            <p>
              Four bedrooms and over 3,900 square feet welcome up to sixteen
              guests, with sun-drenched living spaces, a full chef&rsquo;s
              kitchen, and bedrooms that frame the hills. Wake to the view, linger
              by the pool, and let the estate set the pace.
            </p>
            <Link to="/book-a-stay" className="btn btn--primary">
              Book a Private Stay
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---- Pool, Spa & Outdoor Living ---- */}
      <section className={styles.chapter}>
        <div className={`container ${styles.chapterGrid}`}>
          <Reveal as="div" variant="up" className={styles.chapterText}>
            <p className="eyebrow eyebrow--rule">Pool, Spa &amp; Outdoor Living</p>
            <h2>Resort days, golden-hour nights.</h2>
            <p>
              A resort-style pool and spa, a hillside waterfall, fire pits, and
              wide decks make the outdoors the heart of the estate. It&rsquo;s
              where mornings stretch long, afternoons drift by, and evenings turn
              into something to remember.
            </p>
            <Link to="/gallery" className="link-underline">
              Explore the gallery →
            </Link>
          </Reveal>
          <Reveal as="div" variant="scale" delay={120} className={styles.frame}>
            <Parallax
              speed={0.16}
              className={styles.frameInner}
              style={{ backgroundImage: `url(${mediaUrl(FEATURED.pool)})` }}
            >
              <span />
            </Parallax>
          </Reveal>
        </div>
      </section>

      {/* ---- Gallery preview (best photos only) ---- */}
      <section className="section section--alt">
        <div className="container">
          <Reveal className={styles.sectionHead}>
            <p className="eyebrow eyebrow--rule">A Glimpse</p>
            <h2>The estate, at a glance.</h2>
          </Reveal>
          <div className={styles.galleryGrid}>
            {FEATURED.bestPhotos.map((path, i) => (
              <Reveal
                as={Link}
                key={path}
                variant="scale"
                delay={(i % 3) * 80}
                className={`${styles.galleryItem} ${i === 0 ? styles.galleryItemTall : ""} ${
                  i === FEATURED.bestPhotos.length - 1 ? styles.galleryItemWide : ""
                }`}
                to="/gallery"
                aria-label="View the full gallery"
              >
                <img src={mediaUrl(path)} alt="Villa Rosa" loading="lazy" decoding="async" />
              </Reveal>
            ))}
          </div>
          <div className={styles.galleryCta}>
            <Link to="/gallery" className="btn btn--ghost">
              View the full gallery
            </Link>
          </div>
        </div>
      </section>

      {/* ---- Reviews ---- */}
      <section className="section">
        <div className="container">
          <Reveal className={styles.sectionHead}>
            <p className="eyebrow eyebrow--rule">What our guests say</p>
            <h2>Memories made at Villa Rosa.</h2>
          </Reveal>
          <ElfsightWidget appId="f374a082-a24a-4ea5-a661-018bea42a959" />
        </div>
      </section>

      {/* ---- Inquiry ---- */}
      <section id="inquire" className={`${styles.chapter} ${styles.inquire}`}>
        <div className={`container ${styles.inquireGrid}`}>
          <Reveal as="div" variant="left" className={styles.inquireIntro}>
            <p className="eyebrow eyebrow--rule">Begin Planning</p>
            <h2>Tell us about your celebration.</h2>
            <p>
              Share a few details and our team will be in touch to talk dates,
              availability, and how we can shape Villa Rosa around your vision.
            </p>
            <p className={styles.inquireContact}>
              Prefer email?{" "}
              <a href={`mailto:${SITE.email}`} className="link-underline">
                {SITE.email}
              </a>
            </p>
          </Reveal>
          <Reveal as="div" variant="right" delay={120} className={styles.formCard}>
            <InquiryForm
              type="event"
              extras="home"
              submitLabel="Send Inquiry"
              successTitle="Inquiry received!"
              successBody="Thank you for considering Villa Rosa. Our team will reach out shortly to start planning with you."
            />
          </Reveal>
        </div>
      </section>
    </>
  );
}
